/** W076.1 delivery spine: invite the user to let Memso speak first.
 *
 *  Renders a dismissible one-liner on Today when (a) the browser
 *  supports push, (b) the server has VAPID configured, (c) this
 *  device has no subscription yet, and (d) permission is not denied.
 *  One tap runs the full subscribe dance (permission, service worker,
 *  pushManager, server save) via lib/push.subscribeThisDevice.
 *
 *  Dismissal persists in localStorage so the banner never nags. The
 *  Settings notifications panel remains the durable place to manage
 *  devices; this is just the front-door prompt that makes the morning
 *  brief actually reach a device. */
import { useEffect, useState } from "react";
import { Bell, X as XIcon } from "lucide-react";
import {
  isPushSupported,
  listPushDevices,
  subscribeThisDevice,
} from "../lib/push";

const DISMISS_KEY = "memso.push_banner_dismissed";

type BannerState = "hidden" | "offer" | "working" | "done" | "error";

export function PushEnableBanner() {
  const [state, setState] = useState<BannerState>("hidden");
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!isPushSupported()) return;
        if (localStorage.getItem(DISMISS_KEY) === "1") return;
        if (Notification.permission === "denied") return;
        const devices = await listPushDevices();
        if (cancelled) return;
        if (!devices.enabled) return; // server has no VAPID keys
        if (devices.devices.length > 0) return; // already wired up
        setState("offer");
      } catch {
        /* banner is best-effort; stay hidden on any probe failure */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "hidden") return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setState("hidden");
  };

  const enable = async () => {
    setState("working");
    try {
      await subscribeThisDevice();
      setState("done");
      setMessage("Done. Your morning brief will reach this device.");
      window.setTimeout(() => setState("hidden"), 6000);
    } catch (e) {
      setState("error");
      setMessage(e instanceof Error ? e.message : "Could not enable notifications.");
    }
  };

  return (
    <div className="flex items-center gap-3 rounded-xl border border-indigo-200 dark:border-indigo-900 bg-indigo-50 dark:bg-indigo-950/40 px-4 py-2.5 text-sm">
      <Bell className="w-4 h-4 shrink-0 text-indigo-600 dark:text-indigo-400" />
      {state === "done" || state === "error" ? (
        <span className="flex-1 text-slate-700 dark:text-slate-200">{message}</span>
      ) : (
        <span className="flex-1 text-slate-700 dark:text-slate-200">
          Let Memso reach you first: get the morning brief and time-critical
          nudges as notifications on this device.
        </span>
      )}
      {state === "offer" && (
        <button
          type="button"
          onClick={enable}
          className="rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold px-3 py-1.5 transition-colors"
        >
          Enable
        </button>
      )}
      {state === "working" && (
        <span className="text-xs text-slate-500 dark:text-slate-400">Connecting...</span>
      )}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss notification prompt"
        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
      >
        <XIcon className="w-4 h-4" />
      </button>
    </div>
  );
}
